// src/pages/UserListPage.tsx
import BaseTable from "@/components/BaseTable/BaseTable";
import CustomButton from "@/components/common/Button";
import { getTenantId } from "@/lib/storeManager";
import { TenantUserService } from "@/services/APIs";
import { gridName } from "@/utils/constant/gridName";
import navigationRoutes from "@/utils/navigationRoutes";
import { TenantUser } from "@/utils/types/tenantUser";
import { ProColumns } from "@ant-design/pro-components";
import { useQuery } from "@tanstack/react-query";
import { t } from "i18next";
import { Edit2, Plus } from "lucide-react";
import { useState } from "react";
import { useAppNavigate } from "@/hooks/useAppNavigate";
import { useSearchParams } from "react-router-dom";

const UserListPage: React.FC = () => {
  const navigate = useAppNavigate();
  const tenantId = getTenantId() ?? "";
  const [searchParams] = useSearchParams();
  const urlPage = parseInt(searchParams.get("page") ?? "1", 10);
  const urlPageSize = parseInt(searchParams.get("pageSize") ?? "10", 10);
  const [page, setPage] = useState(urlPage);
  const [pageSize, setPageSize] = useState(urlPageSize);

  const {
    data,
    isLoading,
    refetch: refetchUsers,
  } = useQuery({
    queryKey: ["tenantUsers", tenantId, page, pageSize],
    queryFn: () => TenantUserService.listTenantUsers(page, pageSize),
    enabled: !!tenantId,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    staleTime: 60_000,
  });

  const handleEdit = (id: string) => {
    navigate(navigationRoutes.USER_EDIT(id));
  };

  const columns: ProColumns<TenantUser>[] = [
    {
      title: "User Name",
      render: (_, record) => `${record.first_name} ${record.last_name}`,
      sorter: (a, b) =>
        a.first_name!.localeCompare(b.first_name!) +
        a.last_name!.localeCompare(b.last_name!),
    },
    { title: "Email", dataIndex: "email" },
    { title: "User Type", dataIndex: "user_type" },
    {
      title: "Actions",
      width: 100,
      valueType: "option",
      render: (_, record) => [
        <CustomButton
          key="edit"
          onClick={() => handleEdit(record.id)}
          icon={<Edit2 size={16} />}
          type="text"
          title="Edit User"
        />,
      ],
    },
  ];

  const handleCreate = () => {
    navigate(navigationRoutes.USER_ADD);
  };

  const toolBarRender = () => {
    return [
      <CustomButton
        key="add"
        type="primary"
        onClick={handleCreate}
        title="Add User"
      >
        <Plus size={16} className="mr-1" /> {t("Add User")}
      </CustomButton>,
    ];
  };

  return (
    <BaseTable<TenantUser>
      loading={isLoading}
      columns={columns}
      dataSource={data?.users ?? []}
      page={page}
      pageSize={pageSize}
      total={data?.count}
      onPageChange={(page, pageSize) => {
        setPage(page);
        setPageSize(pageSize ?? 10);
        searchParams.set("page", page.toString());
        searchParams.set("pageSize", pageSize.toString());
        navigate(`${navigationRoutes.USERS}?${searchParams.toString()}`);
        refetchUsers();
      }}
      rowKey="id"
      headerTitle="Users List"
      toolBarRender={data?.permissions?.can_create ? toolBarRender : undefined}
      request={() => refetchUsers()}
      tableName={gridName.userTable}
    />
  );
};

export default UserListPage;
